/**
 * SpawnedTaskDisplay Component
 *
 * Displays a sub-task that was spawned by a tool call.
 * Supports collapsed/expanded views:
 * - Collapsed: Only streaming explanations from latest tool calls (no tool cards)
 * - Expanded: All model_requests with all parts (reuses StepRequestsUser)
 *
 * Uses reactive GraphStore pattern via useNode/useNodes hooks.
 */

import React, { useState, useRef, useCallback } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Loader2 } from 'lucide-react';
import { useNode, useNodes } from '@sourcepilot/framework/relationship-manager';
import { useAgents } from '@sourcepilot/ui/framework/data/hooks/use-agents';
import { useTabData } from '@sourcepilot/ui/framework/state/session-data';
import { StepRequestsUser } from '@sourcepilot/ui/framework/ui/organisms/StepRequestsUser';
import { TextPartRenderer } from '@sourcepilot/ui/shell/chat/TextPartRenderer';
import { usePartDeltaStreaming, useTaskDeltaStream } from '@sourcepilot/ui/framework/data/dag-sync/hooks';
import { JsonCloser, StreamMode } from '@sourcepilot/ui/shell/chat/ToolCallPartRenderer';

interface SpawnedTaskDisplayProps {
  part: any; // ToolCallPart with spawned_task relationship
}

const MAX_COLLAPSED_HEIGHT = 100;

export function SpawnedTaskDisplay({ part }: SpawnedTaskDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const { getAgentByTaskClass } = useAgents();
  const { navigateToTab } = useTabData();

  // REACTIVE: Get spawned task from GraphStore
  const spawnedTask = useNode(part.spawned_task) as any;

  // The spawned task streams on its OWN per-task delta stream — one
  // subscription for all of its parts (mirrors ChatView).
  useTaskDeltaStream(spawnedTask?.id || null);

  // Get parent explanation to use as initial fallback
  const parentExplanation = part.args?.explanation || '';

  // Agent info for the sub-task
  const subTaskLabel = spawnedTask?._label || 'ChatTask';
  const subAgentInfo = getAgentByTaskClass(subTaskLabel);

  // Check if content overflows the max height
  React.useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        setIsOverflowing(contentRef.current.scrollHeight > MAX_COLLAPSED_HEIGHT);
      }
    };
    checkOverflow();
    // Re-check on resize
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  });

  if (!spawnedTask) {
    return null;
  }

  return (
    <div className="mt-1 border border-indigo-200 rounded-md bg-indigo-50 overflow-hidden">
      {/* Header with agent label, open link, and expander */}
      <div className="flex items-center justify-between px-2 py-0.5">
        <div className="flex items-center gap-1">
          {subAgentInfo && (
            <span className="text-[10px] text-indigo-400 font-medium">
              {subAgentInfo.label}
            </span>
          )}
          {spawnedTask?.id && (
            <button
              className="text-indigo-400 hover:text-indigo-600 p-0"
              title="Open sub-agent conversation"
              onClick={(e) => {
                e.stopPropagation();
                navigateToTab({
                  id: spawnedTask.id,
                  title: subAgentInfo?.label || 'Sub-task',
                  contentType: 'chat',
                  taskClassName: subTaskLabel,
                });
              }}
            >
              <ExternalLink className="w-3 h-3" />
            </button>
          )}
        </div>
        <div
          className="cursor-pointer hover:bg-indigo-100 rounded p-0.5"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-indigo-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-indigo-400" />
          )}
        </div>
      </div>

      {/* Content with fade when collapsed and overflowing */}
      <div className="relative">
        <div
          ref={contentRef}
          className="px-2 pb-2"
          style={isExpanded ? undefined : { maxHeight: MAX_COLLAPSED_HEIGHT, overflow: 'hidden' }}
        >
          <TaskThread task={spawnedTask} isExpanded={isExpanded} parentExplanation={parentExplanation} />
        </div>
        {/* Fade gradient only when collapsed AND content overflows */}
        {!isExpanded && isOverflowing && (
          <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-indigo-50 to-transparent pointer-events-none" />
        )}
      </div>
    </div>
  );
}

/**
 * Get thread from task, then render steps
 */
function TaskThread({ task, isExpanded, parentExplanation }: { task: any; isExpanded: boolean; parentExplanation: string }) {
  const thread = useNode(task.thread) as any;

  // Thread data not yet loaded - show parent explanation or thinking spinner
  if (!thread) {
    if (!isExpanded) {
      if (parentExplanation) {
        return (
          <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
            {parentExplanation}
          </div>
        );
      }
      return (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Thinking...</span>
        </div>
      );
    }
    return <div className="text-xs text-indigo-400 italic">Thread data not loaded</div>;
  }

  return <ThreadSteps thread={thread} isExpanded={isExpanded} parentExplanation={parentExplanation} />;
}

/**
 * Get steps from thread, render using existing components
 */
function ThreadSteps({ thread, isExpanded, parentExplanation }: { thread: any; isExpanded: boolean; parentExplanation: string }) {
  const steps = useNodes(thread.steps).orderBy('created_at').value();

  if (steps.length === 0) {
    // No steps yet - show parent explanation or thinking spinner
    if (parentExplanation) {
      return (
        <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
          {parentExplanation}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Thinking...</span>
      </div>
    );
  }

  if (isExpanded) {
    // Expanded: show all steps using StepRequestsUser
    return (
      <div className="space-y-2">
        {steps.map((step: any) => (
          <StepRequestsUser key={step.id} step={step} />
        ))}
      </div>
    );
  }

  // Collapsed: show only last step's last model request
  const lastStep = steps[steps.length - 1];
  return <LastModelRequest step={lastStep} parentExplanation={parentExplanation} />;
}

/**
 * Get the last model request from a step for collapsed view.
 * During streaming: show only streaming explanation text
 * When done: show final content (text parts and explanations)
 */
function LastModelRequest({ step, parentExplanation }: { step: any; parentExplanation: string }) {
  const modelRequests = useNodes(step.model_requests).orderBy('order_index').value();

  if (modelRequests.length === 0) {
    // No model requests yet - show parent explanation or thinking spinner
    if (parentExplanation) {
      return (
        <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
          {parentExplanation}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Thinking...</span>
      </div>
    );
  }

  const lastMr = modelRequests[modelRequests.length - 1];
  return <CollapsedModelRequestView modelRequest={lastMr} parentExplanation={parentExplanation} />;
}

/**
 * Collapsed view of a model request - shows only the latest explanation.
 * Uses a single unified component that handles both streaming and completed states
 * to prevent flashing when transitioning between parts.
 *
 * Once a TextPart (final output) is available, shows that instead of explanations.
 */
function CollapsedModelRequestView({ modelRequest, parentExplanation }: { modelRequest: any; parentExplanation: string }) {
  const modelResponse = useNode(modelRequest.model_response);

  if (!modelResponse) {
    // No model response yet - show parent explanation or thinking spinner
    if (parentExplanation) {
      return (
        <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
          {parentExplanation}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Thinking...</span>
      </div>
    );
  }

  const sortedParts = useNodes((modelResponse as any).parts).orderBy('order_index').value();

  // Check for TextParts (final output) - if present, show those instead of explanations
  const textParts = sortedParts.filter((p: any) => (p._label || p.type) === 'TextPart');
  const hasTextContent = textParts.some((p: any) => p.content && p.content.trim());

  if (hasTextContent) {
    // Show the last text part as final output
    const lastTextPart = textParts[textParts.length - 1] as any;
    return (
      <TextPartRenderer
        part={lastTextPart}
        isStreaming={lastTextPart.is_streaming || false}
      />
    );
  }

  // Get all tool call parts
  const toolCallParts = sortedParts.filter((p: any) => (p._label || p.type) === 'ToolCallPart');

  // Find streaming part (if any)
  const streamingPart = toolCallParts.find((p: any) => p.is_streaming) || null;

  // Get all explanations from completed parts (for fallback)
  const completedExplanations = toolCallParts
    .filter((p: any) => !p.is_streaming && p.args?.explanation)
    .map((p: any) => p.args.explanation);

  // Always use the unified component - it handles both streaming and completed with rate limiting
  return (
    <UnifiedExplanationView
      streamingPart={streamingPart}
      completedExplanations={completedExplanations}
      parentExplanation={parentExplanation}
    />
  );
}

const EXPLANATION_MIN_DISPLAY_TIME = 1500; // 1.5 seconds minimum between explanation changes

/**
 * Unified view that handles both streaming and completed states.
 * Shows streaming explanation when available, falls back to completed explanations.
 *
 * Key: Never clear the displayed explanation until we have new content.
 * Shows thinking spinner when streaming but no content yet.
 * Rate limits explanation changes to at most one every 1.5 seconds.
 */
function UnifiedExplanationView({
  streamingPart,
  completedExplanations,
  parentExplanation
}: {
  streamingPart: any | null;
  completedExplanations: string[];
  parentExplanation: string;
}) {
  const partId = streamingPart?.id;
  const prevPartIdRef = useRef<string | null>(null);
  const jsonParserRef = useRef<JsonCloser | null>(null);

  // Track current streaming content for this specific part
  const [currentPartExplanation, setCurrentPartExplanation] = useState('');
  // Track the last known good explanation (persists across part changes)
  const [lastKnownExplanation, setLastKnownExplanation] = useState('');

  // Rate limiting state - ensures explanations display for at least 1.5s each
  const [displayedExplanation, setDisplayedExplanation] = useState('');
  const lastDisplayTimeRef = useRef<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Reset parser when part changes, but DON'T clear the displayed explanation
  if (partId !== prevPartIdRef.current) {
    prevPartIdRef.current = partId;
    jsonParserRef.current = new JsonCloser(StreamMode.StreamObjectKeyValueTokens);
  }

  // When part changes, save current as last known and reset current
  React.useEffect(() => {
    if (currentPartExplanation) {
      setLastKnownExplanation(currentPartExplanation);
    }
    setCurrentPartExplanation('');
  }, [partId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!jsonParserRef.current) {
    jsonParserRef.current = new JsonCloser(StreamMode.StreamObjectKeyValueTokens);
  }
  const closer = jsonParserRef.current;

  const handleDelta = useCallback((delta: any) => {
    const delta_data = delta.delta?.delta_data;
    const delta_type = delta.delta?.delta_type;
    const args_json_delta = delta_data?.args_delta?.args_json_delta;

    if (args_json_delta && delta_type === 'tool_call_building') {
      closer.append(args_json_delta);
      const [hasChanged, streamingArgsObject] = closer.parse();
      if (hasChanged && streamingArgsObject?.explanation) {
        setCurrentPartExplanation(String(streamingArgsObject.explanation));
      }
    }
  }, [closer]);

  usePartDeltaStreaming(
    streamingPart?.is_streaming ? (partId || null) : null,
    0,
    handleDelta
  );

  // Compute the target explanation (what we want to show)
  const latestCompletedExplanation = completedExplanations.length > 0
    ? completedExplanations[completedExplanations.length - 1]
    : undefined;
  const targetExplanation = currentPartExplanation || latestCompletedExplanation || lastKnownExplanation || parentExplanation || '';

  // Rate-limited display update
  React.useEffect(() => {
    if (!targetExplanation || targetExplanation === displayedExplanation) {
      return;
    }

    const now = Date.now();
    const timeSinceLastDisplay = now - lastDisplayTimeRef.current;

    if (timeSinceLastDisplay >= EXPLANATION_MIN_DISPLAY_TIME) {
      // Can display immediately
      setDisplayedExplanation(targetExplanation);
      lastDisplayTimeRef.current = now;
    } else {
      // Queue for later display
      const delay = EXPLANATION_MIN_DISPLAY_TIME - timeSinceLastDisplay;

      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        setDisplayedExplanation(targetExplanation);
        lastDisplayTimeRef.current = Date.now();
      }, delay);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [targetExplanation, displayedExplanation]);

  // Initialize displayed explanation
  React.useEffect(() => {
    if (!displayedExplanation && targetExplanation) {
      setDisplayedExplanation(targetExplanation);
      lastDisplayTimeRef.current = Date.now();
    }
  }, [targetExplanation, displayedExplanation]);

  // If streaming but no explanation yet, show thinking spinner
  const isStreaming = streamingPart?.is_streaming;
  if (!displayedExplanation && isStreaming) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Thinking...</span>
      </div>
    );
  }

  if (!displayedExplanation) {
    return null;
  }

  return (
    <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
      {displayedExplanation}
    </div>
  );
}
